import { CheckIcon, X, Search } from "lucide-react";
import { useState, useRef, useEffect } from "react";

interface CategorySearchSelectProps {
  categories: string[];
  selectedCategories: string[];
  onChange: (categories: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
}

export default function CategorySearchSelect({
  categories,
  selectedCategories,
  onChange,
  placeholder = "카테고리를 검색하거나 새로 만드세요...",
  disabled = false,
}: CategorySearchSelectProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const filteredCategories = categories.filter((category) =>
    category.toLowerCase().includes(normalizedSearchTerm)
  );

  const trimmedSearchTerm = searchTerm.trim();
  const canCreateNew = trimmedSearchTerm && 
    !categories.some(cat => cat.toLowerCase() === normalizedSearchTerm) &&
    !selectedCategories.some(cat => cat.toLowerCase() === normalizedSearchTerm);

  const allOptions = [
    ...filteredCategories,
    ...(canCreateNew ? [trimmedSearchTerm] : [])
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setFocusedIndex(-1);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setIsOpen(true);
        setFocusedIndex(prev => 
          prev < allOptions.length - 1 ? prev + 1 : 0
        );
        break;
      
      case "ArrowUp":
        e.preventDefault();
        if (isOpen) {
          setFocusedIndex(prev => 
            prev > 0 ? prev - 1 : allOptions.length - 1
          );
        }
        break;
      
      case "Enter":
        e.preventDefault();
        if (isOpen && focusedIndex >= 0 && focusedIndex < allOptions.length) {
          const selectedOption = allOptions[focusedIndex];
          handleAddCategory(selectedOption);
        } else if (!isOpen) {
          setIsOpen(true);
        }
        break;
      
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        setFocusedIndex(-1);
        inputRef.current?.blur();
        break;
      
      case "Tab":
        setIsOpen(false);
        setFocusedIndex(-1);
        break;
    }
  };

  const handleAddCategory = (category: string) => {
    const trimmedCategory = category.trim();
    
    if (trimmedCategory && !selectedCategories.some(cat => 
      cat.toLowerCase() === trimmedCategory.toLowerCase()
    )) {
      onChange([...selectedCategories, trimmedCategory]);
      setSearchTerm(""); // 검색어 초기화
      setFocusedIndex(-1);
      // 드롭다운은 열어둠 (연속 선택 가능)
    }
  };

  const handleRemoveCategory = (categoryToRemove: string) => {
    onChange(selectedCategories.filter(c => c !== categoryToRemove));
  };

  const handleInputFocus = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={`
            w-full pl-10 pr-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white 
            placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 
            focus:border-transparent transition-colors
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls="category-listbox"
          role="combobox"
          aria-describedby="category-help-text"
        />
      </div>

      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-700 rounded-lg shadow-lg max-h-48 overflow-y-auto custom-scrollbar">
          <div id="category-listbox" role="listbox" aria-label="카테고리 목록">
            {filteredCategories.map((category, index) => {
              const isSelected = selectedCategories.includes(category);
              const isFocused = focusedIndex === index;
              
              return (
                <button
                  key={category}
                  onClick={() => handleAddCategory(category)}
                  className={`
                    w-full text-left px-3 py-2 flex justify-between items-center transition-colors
                    ${isFocused ? 'bg-slate-600' : 'hover:bg-slate-700'}
                    ${isSelected ? 'text-green-300' : 'text-white'}
                  `}
                  role="option"
                  aria-selected={isSelected}
                >
                  <span>{category}</span>
                  {isSelected && (
                    <CheckIcon className="w-4 h-4 text-green-500" />
                  )}
                </button>
              );
            })}

            {canCreateNew && (
              <button
                onClick={() => handleAddCategory(trimmedSearchTerm)}
                className={`
                  w-full text-left px-3 py-2 text-blue-400 border-t border-slate-700 transition-colors
                  ${focusedIndex === filteredCategories.length ? 'bg-slate-600' : 'hover:bg-slate-700'}
                `}
                role="option"
                aria-selected={false}
              >
                <span className="flex items-center">
                  <span className="text-blue-500 mr-2">+</span>
                  "{trimmedSearchTerm}" 카테고리 새로 만들기
                </span>
              </button>
            )}

            {filteredCategories.length === 0 && !canCreateNew && (
              <div className="px-3 py-2 text-slate-500 text-sm text-center">
                {searchTerm ? "검색 결과가 없습니다." : "사용 가능한 카테고리가 없습니다."}
              </div>
            )}
          </div>
        </div>
      )}

      {/* 선택된 카테고리 태그들 */}
      {selectedCategories.length > 0 && (
        <div className="mt-3">
          <p className="text-xs text-slate-400 mb-2">
            선택된 카테고리 ({selectedCategories.length}개)
          </p>
          <div className="flex flex-wrap gap-2">
            {selectedCategories.map((category) => (
              <span
                key={category}
                className="inline-flex items-center px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-full transition-colors"
              >
                <span>{category}</span>
                {!disabled && (
                  <button
                    onClick={() => handleRemoveCategory(category)}
                    className="ml-2 hover:text-red-300 transition-colors"
                    title={`${category} 제거`}
                    type="button"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
